/**
 * ProfileContainer — container component that wires useProfile to ProfileForm.
 *
 * Handles loading state (spinner while fetching), error display, and
 * delegates form rendering + submission to the presentational ProfileForm.
 *
 * Pattern: container/presentational (ADR-3). This component owns the network
 * state; ProfileForm owns the UI state.
 */

import { useState, useCallback } from "react";
import type { HealthProfile } from "@autodieta/shared";
import { useProfile } from "./useProfile.js";
import { ProfileForm } from "./ProfileForm.js";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ProfileContainerProps {
  workerUrl: string;
}

// ---------------------------------------------------------------------------
// ProfileContainer
// ---------------------------------------------------------------------------

export function ProfileContainer({ workerUrl }: ProfileContainerProps): JSX.Element {
  const { profile, loading, error, saved, save } = useProfile(workerUrl);
  const [formState, setFormState] = useState<Partial<HealthProfile> | null>(null);

  // Initialize form state from fetched profile (only once)
  const effectiveFormState: Partial<HealthProfile> = formState ?? profile ?? {};

  const handleChange = useCallback((updated: Partial<HealthProfile>) => {
    setFormState(updated);
  }, []);

  const handleSubmit = useCallback(
    async (value: Partial<HealthProfile>) => {
      await save(value);
    },
    [save],
  );

  // Loading spinner (initial fetch)
  if (loading && profile === null && !error) {
    return (
      <div
        className="flex min-h-[200px] items-center justify-center"
        aria-busy="true"
        aria-label="Cargando perfil"
      >
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-600 border-t-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h2 className="text-xl font-bold text-zinc-100">Mi perfil de salud</h2>
      <p className="text-sm text-zinc-400">
        Completar tu perfil permite al agente personalizar mejor tus planes.
      </p>

      <ProfileForm
        value={effectiveFormState}
        onChange={handleChange}
        onSubmit={(v) => void handleSubmit(v)}
        saving={loading}
        {...(error !== undefined ? { error } : {})}
        {...(saved ? { successMessage: "Perfil guardado correctamente." } : {})}
      />
    </div>
  );
}
